import { useState, useEffect, useCallback } from 'react';

/**
 * Hook pour gérer l'état de la sidebar (collapsed/expanded)
 * - Persiste la préférence dans localStorage
 * - Détecte automatiquement le mode mobile
 * - Gère l'ouverture/fermeture du menu mobile
 */
export const useSidebarState = () => {
  // État collapsed (desktop) - persisté en localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem('sidebar-collapsed');
    return stored === 'true';
  });

  // État ouvert du menu mobile
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Détection du mode mobile
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });

  // Écouter les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Fermer le menu mobile si on passe en desktop
      if (!mobile && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileOpen]);

  // Fermer le menu mobile avec Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    if (isMobileOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Empêcher le scroll du body quand le menu est ouvert
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  // Toggle sidebar (desktop)
  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebar-collapsed', String(newValue));
      return newValue;
    });
  }, []);

  // Ouvrir la sidebar (desktop)
  const openSidebar = useCallback(() => {
    setIsCollapsed(false);
    localStorage.setItem('sidebar-collapsed', 'false');
  }, []);

  // Fermer la sidebar (desktop)
  const closeSidebar = useCallback(() => {
    setIsCollapsed(true);
    localStorage.setItem('sidebar-collapsed', 'true');
  }, []);

  // Ouvrir le menu mobile
  const openMobileMenu = useCallback(() => {
    setIsMobileOpen(true);
  }, []);

  // Fermer le menu mobile
  const closeMobileMenu = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  // Toggle menu mobile
  const toggleMobileMenu = useCallback(() => {
    setIsMobileOpen(prev => !prev);
  }, []);

  return {
    // États
    isCollapsed,
    isMobileOpen,
    isMobile,

    // Actions desktop
    toggleSidebar,
    openSidebar,
    closeSidebar,

    // Actions mobile
    openMobileMenu,
    closeMobileMenu,
    toggleMobileMenu,
    setIsMobileOpen
  };
};

export default useSidebarState;
